// Live market data — Binance first, CoinGecko as fallback. Used by the asset
// detail screen for the current price, 24h change and the price chart. No mock
// fallback: on total failure the query errors and the UI shows a loading state.

const BINANCE = 'https://api.binance.com/api/v3';
const CG = 'https://api.coingecko.com/api/v3';

/** App symbol → Binance USDT pair (null when not tradable, e.g. USDT itself). */
const BINANCE_PAIR: Record<string, string | null> = { TRX: 'TRXUSDT', USDT: null };
/** App symbol → CoinGecko id (fallback source). */
const COIN_ID: Record<string, string> = { TRX: 'tron', USDT: 'tether' };

export interface CoinPrice {
  usd: number;
  /** 24h change as a fraction (0.021 = +2.1%). */
  change24h: number;
}

/** Current USD price + change. Binance first, CoinGecko fallback.
 *  Change uses the Binance trading-day window (since 00:00 UTC) so it matches the
 *  percentage shown in the Binance app, rather than a rolling-24h figure. */
export async function fetchPrice(symbol: string): Promise<CoinPrice> {
  const pair = BINANCE_PAIR[symbol];
  if (pair) {
    try {
      const r = await fetch(`${BINANCE}/ticker/tradingDay?symbol=${pair}`);
      if (r.ok) {
        const j = (await r.json()) as { lastPrice: string; priceChangePercent: string };
        return { usd: parseFloat(j.lastPrice), change24h: parseFloat(j.priceChangePercent) / 100 };
      }
    } catch {
      /* fall through to CoinGecko */
    }
  }
  const id = COIN_ID[symbol];
  if (!id) throw new Error(`unknown symbol ${symbol}`);
  const r = await fetch(`${CG}/simple/price?ids=${id}&vs_currencies=usd&include_24hr_change=true`);
  if (!r.ok) throw new Error(`price ${r.status}`);
  const j = (await r.json()) as Record<string, { usd: number; usd_24h_change?: number }>;
  const d = j[id];
  if (!d) throw new Error('price missing');
  return { usd: d.usd, change24h: (d.usd_24h_change ?? 0) / 100 };
}

export interface ChartPoint {
  /** epoch ms */
  t: number;
  /** price in USD */
  p: number;
}

/** Binance kline (interval, limit) per range. */
const BINANCE_KLINE: Record<string, { interval: string; limit: number }> = {
  '1d': { interval: '15m', limit: 96 },
  '1w': { interval: '1h', limit: 168 },
  '1m': { interval: '4h', limit: 180 },
  '3m': { interval: '12h', limit: 180 },
  '1y': { interval: '1d', limit: 365 },
  all: { interval: '1w', limit: 1000 },
};
const CG_DAYS: Record<string, string> = {
  all: 'max',
  '1y': '365',
  '3m': '90',
  '1m': '30',
  '1w': '7',
  '1d': '1',
};

/** USD price series for a coin over a range. Binance first, CoinGecko fallback. */
export async function fetchChart(symbol: string, range: string): Promise<ChartPoint[]> {
  const pair = BINANCE_PAIR[symbol];
  if (pair) {
    try {
      const k = BINANCE_KLINE[range] ?? BINANCE_KLINE['1m']!;
      const r = await fetch(`${BINANCE}/klines?symbol=${pair}&interval=${k.interval}&limit=${k.limit}`);
      if (r.ok) {
        const rows = (await r.json()) as [number, string, string, string, string, ...unknown[]][];
        return rows.map((row) => ({ t: row[0], p: parseFloat(row[4]) }));
      }
    } catch {
      /* fall through to CoinGecko */
    }
  }
  const id = COIN_ID[symbol];
  if (!id) return [];
  const days = CG_DAYS[range] ?? '30';
  const r = await fetch(`${CG}/coins/${id}/market_chart?vs_currency=usd&days=${days}`);
  if (!r.ok) throw new Error(`chart ${r.status}`);
  const j = (await r.json()) as { prices: [number, number][] };
  return j.prices.map(([t, p]) => ({ t, p }));
}
